import React, { useEffect, useState } from "react"
import { Modal, Table } from "react-bootstrap"

import Api from "@/api"
import LoadingSpinner from "@/components/LoadingSpinner"

interface FieldChange {
  fieldName: string
  previousValue: string | null
  newValue: string | null
  details?: Record<string, string>
}

type ProposalStatus = "upcoming" | "accepted" | "not_adopted"

// Set only for changes made on a details-review document: whether the cruise line has
// replied to the operator's proposal yet.
const PROPOSAL_STATUS_BADGES: Record<ProposalStatus, { label: string; className: string }> = {
  upcoming: { label: "Upcoming Changes", className: "text-bg-warning" },
  accepted: { label: "Accepted Changes", className: "text-bg-success" },
  not_adopted: { label: "Changes Not Adopted", className: "text-bg-secondary" },
}

// One entry per event, which is one user action: a section save that touched five
// fields is a single entry carrying five changes.
interface ChangeSetEntry {
  key: string
  type: "changes"
  actor: string
  company: string
  occurredAt: string
  changes: FieldChange[]
  scope?: string | null
  proposalStatus?: ProposalStatus | null
}

interface LifecycleEntry {
  key: string
  type: "lifecycle"
  actor: string
  company: string
  occurredAt: string
  description: string
  details?: Record<string, string>
  scope?: string | null
}

type AuditEntry = ChangeSetEntry | LifecycleEntry

const TourChangeLogModal = ({
  show,
  onHide,
  productId,
}: {
  show: boolean
  onHide: () => void
  productId: string
}): JSX.Element => {
  const [entries, setEntries] = useState<AuditEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [retryCount, setRetryCount] = useState(0)

  useEffect(() => {
    if (!show) return

    setLoading(true)
    setError(null)

    Api.onboardingProduct
      .fetchAudit(productId)
      .then((res) => setEntries(res.data.data.entries))
      .catch(() => setError("Could not load change log. Please try again."))
      .finally(() => setLoading(false))
  }, [show, retryCount])

  const humanizeKey = (key: string) =>
    key.replace(/([A-Z])/g, " $1").replace(/^[a-z]/, (c) => c.toUpperCase())

  // Each label sits on its own line above its value: with the value inline, a long
  // multi-line blob pushed the *next* label alongside it, so "New value:" read as if
  // it belonged to the previous block.
  const renderDetails = (details: Record<string, string>) => (
    <div className="mt-1">
      {Object.keys(details).map((label) => {
        const value = details[label]
        const multiLine = value.includes("\n")
        return (
          <div key={label} className="small text-muted">
            <div>
              <strong>{humanizeKey(label)}:</strong>
              {!multiLine && <> {value}</>}
            </div>
            {multiLine && (
              <pre className="mt-1 mb-2 small text-muted" style={{ whiteSpace: "pre-wrap" }}>
                {value}
              </pre>
            )}
          </div>
        )
      })}
    </div>
  )

  const expandIcon = <i className="bi bi-chevron-down ms-1 small text-muted" />

  const renderChange = (change: FieldChange) => {
    // "Not defined" only on the previous side: it says the field had never been set,
    // where an em dash on the new side means the user cleared it.
    const summary = (
      <>
        <strong>{change.fieldName}</strong>{" "}
        <span className="text-muted">{change.previousValue ?? "Not defined"}</span>
        {" → "}
        {change.newValue ?? "—"}
      </>
    )

    if (!change.details) return summary

    return (
      <details>
        <summary className="d-inline" style={{ cursor: "pointer" }}>
          {summary}
          {expandIcon}
        </summary>
        {renderDetails(change.details)}
      </details>
    )
  }

  const renderProposalStatus = (status: ProposalStatus) => {
    const badge = PROPOSAL_STATUS_BADGES[status]

    if (!badge) return null

    return <span className={`badge ${badge.className} mb-1`}>{badge.label}</span>
  }

  const renderEntry = (entry: AuditEntry) => {
    if (entry.type === "changes") {
      return (
        <>
          {entry.proposalStatus && renderProposalStatus(entry.proposalStatus)}
          {/* A field can only change once per event, so name + event id is unique. */}
          {entry.changes.map((change) => (
            <div key={`${entry.key}/${change.fieldName}`}>{renderChange(change)}</div>
          ))}
        </>
      )
    }

    if (entry.details) {
      return (
        <details>
          <summary className="d-inline" style={{ cursor: "pointer" }}>
            {entry.description}
            {expandIcon}
          </summary>
          {renderDetails(entry.details)}
        </details>
      )
    }

    return entry.description
  }

  const renderBody = () => {
    if (loading) {
      return (
        <div className="d-flex justify-content-center py-4">
          <LoadingSpinner />
        </div>
      )
    }

    if (error) {
      return (
        <div className="text-center py-4">
          <p className="text-danger mb-3">{error}</p>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => setRetryCount((c) => c + 1)}
          >
            Retry
          </button>
        </div>
      )
    }

    if (entries.length === 0) {
      return <p className="text-muted py-4 text-center">No changes recorded yet.</p>
    }

    return (
      <Table hover size="sm" className="mb-0">
        <thead>
          <tr>
            <th>User</th>
            <th>Company</th>
            <th>Time</th>
            <th>Change</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <tr key={entry.key}>
              <td className="text-nowrap pe-4">{entry.actor}</td>
              <td className="text-nowrap pe-4">{entry.company}</td>
              <td className="text-nowrap pe-4">{entry.occurredAt}</td>
              <td>
                {renderEntry(entry)}
                {entry.scope && <div className="small text-muted mt-1">Period: {entry.scope}</div>}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    )
  }

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Tour Change Log</Modal.Title>
      </Modal.Header>
      <Modal.Body>{renderBody()}</Modal.Body>
    </Modal>
  )
}

export default TourChangeLogModal
